/**
 * Журнал «Приход ТМЦ» (ОСНОВА.МТО, миграция 0341): поступления материалов
 * по счетам-фактурам с объектом. Ведёт отдел МТО, материалы БДР Экономист
 * берёт отсюда. Деньги с бэкенда приходят строкой («2418.03») — точные, без
 * округлений float; складываются на бэкенде.
 */

/** Вид материала: по проектной документации или прочие. */
export type MaterialKind = 'project' | 'other';

export const MATERIAL_KIND_LABEL: Readonly<Record<MaterialKind, string>> = {
  project: 'По проекту',
  other: 'Прочие',
};

export interface Receipt {
  id: string;
  receipt_date: string;
  invoice_no: string | null;
  act_no: string | null;
  ttn_no: string | null;
  tn_no: string | null;
  payment_invoice_no: string | null;
  supplier: string;
  item_name: string;
  item_size: string | null;
  quantity: string | null;
  unit: string | null;
  price: string | null;
  amount: string | null;
  vat_amount: string | null;
  vat_free: boolean;
  amount_with_vat: string | null;
  object_name: string | null;
  object_id: string | null;
  /** Куда сопоставлен объект: объект ОСНОВЫ или код центра затрат. */
  object_target: string | null;
  material_kind: MaterialKind | null;
  signed_by: string | null;
  note: string | null;
  original_sent_on: string | null;
  uptk_info: string | null;
  uptk_upd_no: string | null;
  transferred_to: string | null;
  transfer_act: string | null;
  issues: string[];
  source: 'import' | 'manual';
  import_id: string | null;
  source_sheet: string | null;
  source_row: number | null;
  source_no: string | null;
  created_at: string;
  updated_by: string | null;
  updated_by_name: string | null;
  updated_at: string;
}

export interface ReceiptPage {
  items: Receipt[];
  total: number;
  amount: string;
  vat_amount: string;
  amount_with_vat: string;
}

export interface ReceiptFilter {
  year?: number;
  month?: number;
  object_id?: string;
  unmapped?: boolean;
  with_issues?: boolean;
  material_kind?: MaterialKind;
  q?: string;
  limit?: number;
  offset?: number;
}

/** Тело создания и правки строки: числа — как ввёл человек. */
export interface ReceiptInput {
  receipt_date: string;
  invoice_no?: string | null;
  act_no?: string | null;
  ttn_no?: string | null;
  tn_no?: string | null;
  payment_invoice_no?: string | null;
  supplier: string;
  item_name: string;
  item_size?: string | null;
  quantity?: string | null;
  unit?: string | null;
  price?: string | null;
  amount?: string | null;
  vat_amount?: string | null;
  vat_free?: boolean;
  amount_with_vat?: string | null;
  object_name?: string | null;
  signed_by?: string | null;
  note?: string | null;
  original_sent_on?: string | null;
  uptk_info?: string | null;
  uptk_upd_no?: string | null;
  transferred_to?: string | null;
  transfer_act?: string | null;
}

export interface SummaryCell {
  object_id: string | null;
  object_name: string | null;
  project_id: string | null;
  econ_object_id: string | null;
  target_name: string | null;
  material_kind: MaterialKind | null;
  is_archive: boolean;
  mapped_by_rule: boolean;
  month: number;
  rows: number;
  amount: string;
  amount_with_vat: string;
}

export interface ReceiptSummary {
  year: number;
  years: number[];
  cells: SummaryCell[];
}

export interface ReceiptObject {
  id: string;
  name: string;
  name_key: string;
  project_id: string | null;
  project_name: string | null;
  econ_object_id: string | null;
  econ_object_code: string | null;
  material_kind: MaterialKind;
  is_archive: boolean;
  mapped_by_rule: boolean;
  comment: string | null;
  updated_at: string;
  rows_total: number;
  amount_total: string | null;
  rows_year: number;
  amount_year: string | null;
  first_date: string | null;
  last_date: string | null;
}

export interface TargetOptions {
  projects: { id: string; name: string; code: string | null }[];
  cost_centers: { id: string; code: string; name: string | null }[];
}

export interface ReceiptObjectsResponse {
  year: number;
  items: ReceiptObject[];
  targets: TargetOptions;
}

export interface ObjectPatch {
  target?: { project_id?: string | null; econ_object_id?: string | null };
  material_kind?: MaterialKind;
  is_archive?: boolean;
  comment?: string | null;
  confirm?: boolean;
}

export interface HistoryEntry {
  id: number;
  entity: 'receipt' | 'object';
  entity_id: string;
  action: 'create' | 'update' | 'delete' | 'restore';
  diff: Record<string, { from: string | null; to: string | null }>;
  actor: string | null;
  actor_name: string | null;
  at: string;
}

export interface PreviewSheet {
  name: string;
  year: number | null;
  header_row: number | null;
  columns: string[];
  rows: number;
  amount: string;
  amount_with_vat: string;
  date_from: string | null;
  date_to: string | null;
  issues: number;
  issue_samples: { row: number; text: string }[];
  skipped: number;
  note: string | null;
  imported: number;
  edited: number;
}

export interface PreviewObject {
  name: string;
  key: string;
  rows: number;
  amount: string;
  material_kind: MaterialKind;
  known: boolean;
}

export interface ReceiptsPreview {
  filename: string;
  sha256: string;
  sheets: PreviewSheet[];
  objects: PreviewObject[];
  rows: number;
  amount: string;
  warnings: string[];
}

export interface ImportSheetResult {
  sheet: string;
  rows_in_file: number;
  replaced: number;
  inserted: number;
  kept_edited: number;
  amount: string;
}

export interface ImportResult {
  import_id: string;
  sheets: ImportSheetResult[];
  inserted: number;
  replaced: number;
  new_objects: number;
}

export const MONTHS_SHORT = [
  'янв',
  'фев',
  'мар',
  'апр',
  'май',
  'июн',
  'июл',
  'авг',
  'сен',
  'окт',
  'ноя',
  'дек',
] as const;

export const MONTHS_FULL = [
  'январь',
  'февраль',
  'март',
  'апрель',
  'май',
  'июнь',
  'июль',
  'август',
  'сентябрь',
  'октябрь',
  'ноябрь',
  'декабрь',
] as const;

const RUB = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const RUB0 = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });
const QTY = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 4 });

/** Деньги «2418.03» → «2 418,03». Пусто — тире. */
export function formatMoney(v: string | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) ? RUB.format(n) : v;
}

/** Деньги в сводах — без копеек, «2 418». */
export function formatMoney0(v: string | number | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? RUB0.format(n) : String(v);
}

/** Миллионы рублей с одним знаком: «91,9 млн». */
export function formatMillions(v: string | number): string {
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return String(v);
  return `${(n / 1_000_000).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} млн`;
}

export function formatQty(v: string | null | undefined): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  return Number.isFinite(n) ? QTY.format(n) : v;
}

/** Дата «2026-01-05» → «05.01.2026». */
export function formatDate(v: string | null | undefined): string {
  if (!v) return '—';
  const [y, m, d] = v.slice(0, 10).split('-');
  return y && m && d ? `${d}.${m}.${y}` : v;
}

/** Куда сопоставлен объект прихода — подпись. */
export function targetLabel(o: {
  project_name?: string | null;
  econ_object_code?: string | null;
}): string | null {
  return o.project_name ?? o.econ_object_code ?? null;
}

/** Подписи полей для журнала «было → стало». */
export const RECEIPT_FIELD_LABEL: Readonly<Record<string, string>> = {
  receipt_date: 'Дата поступления',
  invoice_no: 'Счёт-фактура',
  act_no: 'Акт',
  ttn_no: 'ТТН',
  tn_no: 'ТН',
  payment_invoice_no: 'Счёт на оплату',
  supplier: 'Поставщик',
  item_name: 'Наименование',
  item_size: 'Размер',
  quantity: 'Количество',
  unit: 'Ед. изм.',
  price: 'Цена',
  amount: 'Сумма без НДС',
  vat_amount: 'НДС',
  vat_free: 'Без НДС',
  amount_with_vat: 'Сумма с НДС',
  object_name: 'Объект',
  signed_by: 'Кто принял',
  note: 'Примечание',
  original_sent_on: 'Оригинал в бухгалтерию',
  uptk_info: 'Доп. информация (УПТК)',
  uptk_upd_no: '№ УПД (УПТК)',
  transferred_to: 'Передано',
  transfer_act: '№ акта передачи',
  project_id: 'Объект ОСНОВЫ',
  econ_object_id: 'Центр затрат',
  material_kind: 'Вид материала',
  is_archive: 'Архив',
  mapped_by_rule: 'Предложено программой',
  comment: 'Комментарий',
};
